import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as returnService from '../../services/returnService';
import { ReturnResponse, ReturnRequest } from '../../types/return';
import { ReturnStatus, ReturnType, ReturnApprove, ReturnReject } from '../../services/returnService';

interface ReturnState {
  returns: ReturnResponse[];
  selectedReturn: ReturnResponse | null;
  loading: boolean;
  error: string | null;
}

const initialState: ReturnState = {
  returns: [],
  selectedReturn: null,
  loading: false,
  error: null,
};

export const fetchReturnsAsync = createAsyncThunk(
  'returns/fetchReturns',
  async (params?: {
    branch_id?: number;
    status?: ReturnStatus;
    return_type?: ReturnType;
    start_date?: string;
    end_date?: string;
    page?: number;
    page_size?: number;
  }) => {
    const response = await returnService.fetchReturns(params);
    return response;
  }
);

export const getReturnByIdAsync = createAsyncThunk(
  'returns/getReturnById',
  async (id: number) => {
    const response = await returnService.getReturnById(id);
    return response;
  }
);

export const createReturnAsync = createAsyncThunk(
  'returns/createReturn',
  async (returnRequest: ReturnRequest, { rejectWithValue }) => {
    try {
      const response = await returnService.createReturn(returnRequest);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to create return');
    }
  }
);

export const approveReturnAsync = createAsyncThunk(
  'returns/approveReturn',
  async ({ id, data }: { id: number; data?: ReturnApprove }, { rejectWithValue }) => {
    try {
      const response = await returnService.approveReturn(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to approve return');
    }
  }
);

export const rejectReturnAsync = createAsyncThunk(
  'returns/rejectReturn',
  async ({ id, data }: { id: number; data: ReturnReject }, { rejectWithValue }) => {
    try {
      const response = await returnService.rejectReturn(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to reject return');
    }
  }
);

const returnSlice = createSlice({
  name: 'returns',
  initialState,
  reducers: {
    clearSelectedReturn(state) {
      state.selectedReturn = null;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Returns
      .addCase(fetchReturnsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReturnsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.returns = action.payload;
      })
      .addCase(fetchReturnsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch returns';
      })
      // Get Return by ID
      .addCase(getReturnByIdAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(getReturnByIdAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedReturn = action.payload;
      })
      .addCase(getReturnByIdAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Return not found';
      })
      // Create Return
      .addCase(createReturnAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createReturnAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.returns.push(action.payload);
      })
      .addCase(createReturnAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to create return';
      })
      // Approve Return
      .addCase(approveReturnAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(approveReturnAsync.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.returns.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.returns[index] = action.payload;
        }
        if (state.selectedReturn?.id === action.payload.id) {
          state.selectedReturn = action.payload;
        }
      })
      .addCase(approveReturnAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to approve return';
      })
      // Reject Return
      .addCase(rejectReturnAsync.fulfilled, (state, action) => {
        const index = state.returns.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.returns[index] = action.payload;
        }
        if (state.selectedReturn?.id === action.payload.id) {
          state.selectedReturn = action.payload;
        }
      });
  },
});

export const { clearSelectedReturn, clearError } = returnSlice.actions;

// Selectors
export const selectReturns = (state: any) => state.returns.returns;
export const selectSelectedReturn = (state: any) => state.returns.selectedReturn;
export const selectLoading = (state: any) => state.returns.loading;
export const selectError = (state: any) => state.returns.error;

export default returnSlice.reducer;